import { useState, useMemo } from 'react'
import { Popover, Button, Input, VStack, HStack, Text, Badge, Box, Spinner } from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi'
import { useTags } from '../../hooks/useTag'
import type { TagSummaryDto } from '../../types/tag'

interface TagPickerProps {
  excludeTagIds?: string[]
  onSelect: (tag: TagSummaryDto) => void
  triggerLabel?: string
  isDisabled?: boolean
  assignableOnly?: boolean
}

const tagTypeColors: Record<string, string> = {
  Volunteer: 'green',
  Donor: 'purple',
  Activist: 'orange',
  Issue: 'blue',
  Member: 'teal',
  Leader: 'yellow',
  Supporter: 'cyan',
  Opposition: 'red',
  Undecided: 'gray',
  Custom: 'pink',
}

export function TagPicker({
  excludeTagIds = [],
  onSelect,
  triggerLabel = 'Add Tag',
  isDisabled = false,
  assignableOnly = true,
}: TagPickerProps) {
  const { data: tags, isLoading } = useTags()
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const filteredTags = useMemo(() => {
    if (!tags) return []
    return tags.filter(tag => {
      if (!tag.isActive) return false
      if (assignableOnly && !tag.canBeAssigned) return false
      if (excludeTagIds.includes(tag.id)) return false
      if (search && !tag.label.toLowerCase().includes(search.toLowerCase())) return false
      return true
    })
  }, [tags, excludeTagIds, search, assignableOnly])

  const handleSelect = (tag: TagSummaryDto) => {
    onSelect(tag)
    setIsOpen(false)
    setSearch('')
  }

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={e => {
        if (e.open) {
          setIsOpen(true);
        } else
          { setIsOpen(false); setSearch('') }
      }}>
      <Popover.Trigger asChild>
        <Button size="sm" colorPalette="brand" variant="outline" disabled={isDisabled}><FiPlus />{triggerLabel}</Button>
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content w="320px">
          <Popover.Title px={3} py={2}>
            <Input
              size="sm"
              placeholder="Search tags..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </Popover.Title>
          <Popover.Body maxH="240px" overflowY="auto" p={0}>
            {isLoading && (
              <VStack py={4}>
                <Spinner size="sm" color="accent" />
              </VStack>
            )}
            {!isLoading && filteredTags.length === 0 && (
              <Box px={3} py={4} textAlign="center">
                <Text fontSize="sm" color="gray.500">No tags available</Text>
              </Box>
            )}
            {filteredTags.map(tag => (
              <HStack
                key={tag.id}
                px={3}
                py={2}
                _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                onClick={() => handleSelect(tag)}
              >
                <Badge
                  colorPalette={tagTypeColors[tag.tagType ?? ''] ?? 'gray'}
                  fontSize="xs"
                >
                  {tag.tagType || 'Tag'}
                </Badge>
                <Text fontSize="sm">{tag.label}</Text>
              </HStack>
            ))}
          </Popover.Body>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
}
